import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  AlertTriangle,
  CheckCircle2,
  Clock3,
  Database,
  Loader2,
  RefreshCw,
  RotateCcw,
  Upload,
} from "lucide-react"

import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@f2e/ui/components/base"

import { useToast } from "@/hooks"
import {
  type TabularCapabilitiesSchema,
  type TabularDetailSchema,
  useKnowledgeBaseTabularCapabilitiesApi,
  useKnowledgeBaseTabularDetailApi,
  useRefreshKnowledgeBaseTabularSourceApi,
  useReplaceKnowledgeBaseTabularSourceApi,
  useRetryKnowledgeBaseTabularSourceApi,
} from "@/hooks/api"
import { getUserFacingApiErrorToastProps } from "@/lib/apiErrors"
import {
  getTabularFileExtension,
  getTabularFileValidationFailure,
} from "@/lib/fileUtils"

import { getTabularSourceKind } from "./tabular"
import type { KnowledgeNode } from "./types"
import { useUiTranslation } from "@f2e/ui/i18n"

interface TabularFileDetailProps {
  node: KnowledgeNode
  organizationId: string
  workspaceId: string
  canEdit: boolean
  onLifecycleChange?: () => Promise<unknown> | unknown
}

type LifecycleAction = "replace" | "refresh" | "retry"

const isProcessing = (detail: TabularDetailSchema | null) =>
  detail?.processing_state === "PENDING" ||
  detail?.processing_state === "PROCESSING"

export const TabularFileDetail = ({
  node,
  organizationId,
  workspaceId,
  canEdit,
  onLifecycleChange,
}: TabularFileDetailProps) => {
  const { t } = useTranslation()
  const { t: uiT } = useUiTranslation()
  const translationRef = useRef(t)
  useEffect(() => {
    translationRef.current = t
  }, [t])
  const { alertToast, successToast } = useToast()
  const [detail, setDetail] = useState<TabularDetailSchema | null>(null)
  const [capabilities, setCapabilities] =
    useState<TabularCapabilitiesSchema | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [candidateFile, setCandidateFile] = useState<File | null>(null)
  const [isReplaceDialogOpen, setIsReplaceDialogOpen] = useState(false)
  const [isMutating, setIsMutating] = useState(false)
  const activeActionRef = useRef<LifecycleAction | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { fetchFn: fetchDetail } = useKnowledgeBaseTabularDetailApi()
  const { fetchFn: fetchCapabilities } =
    useKnowledgeBaseTabularCapabilitiesApi()
  const { fetchFn: replaceSource } = useReplaceKnowledgeBaseTabularSourceApi()
  const { fetchFn: refreshSource } = useRefreshKnowledgeBaseTabularSourceApi()
  const { fetchFn: retrySource } = useRetryKnowledgeBaseTabularSourceApi()
  const sourceKind = getTabularSourceKind(node)
  const loadFailedTitle = t("knowledgeBase.treeView.tabular.loadFailed")
  const loadFailedDescription = t(
    "knowledgeBase.treeView.tabular.loadFailedDescription"
  )
  const urlBuilderParams = useMemo(
    () => ({
      organizationId,
      workspaceId,
      kbId: node.knowledge_base_id,
      nodeId: node.id,
    }),
    [node.id, node.knowledge_base_id, organizationId, workspaceId]
  )

  const loadDetail = useCallback(
    async (preserveData = false) => {
      const response = await fetchDetail({
        urlBuilderParams,
        preserveData,
      })
      const nextDetail = response?.data ?? null
      setDetail(nextDetail)
      return nextDetail
    },
    [fetchDetail, urlBuilderParams]
  )

  useEffect(() => {
    let active = true
    setIsLoading(true)
    Promise.all([
      loadDetail(),
      fetchCapabilities({
        urlBuilderParams: { organizationId, workspaceId },
      }),
    ])
      .then(([, capabilityResponse]) => {
        if (active) setCapabilities(capabilityResponse?.data ?? null)
      })
      .catch((error) => {
        if (!active) return
        console.error("[KB] load tabular detail failed", error)
        alertToast({
          ...getUserFacingApiErrorToastProps(error, translationRef.current, {
            title: loadFailedTitle,
            fallbackDescription: loadFailedDescription,
          }),
        })
      })
      .finally(() => {
        if (active) setIsLoading(false)
      })
    return () => {
      active = false
    }
  }, [
    alertToast,
    fetchCapabilities,
    loadFailedDescription,
    loadFailedTitle,
    loadDetail,
    organizationId,
    workspaceId,
  ])

  const finishLifecycleAction = useCallback(
    (action: LifecycleAction, nextDetail: TabularDetailSchema) => {
      activeActionRef.current = null
      if (nextDetail.processing_state === "FAILED") {
        alertToast({
          title: t("knowledgeBase.treeView.tabular.actionFailed"),
          description: t(
            nextDetail.available
              ? "knowledgeBase.treeView.tabular.failureWithLastGood"
              : "knowledgeBase.treeView.tabular.failureWithoutLastGood"
          ),
        })
        return
      }
      successToast({
        description: t(
          action === "replace" ||
            (action === "retry" && sourceKind !== "googleSheet")
            ? "knowledgeBase.treeView.tabular.replaceSuccess"
            : "knowledgeBase.treeView.tabular.refreshSuccess"
        ),
      })
    },
    [alertToast, sourceKind, successToast, t]
  )

  useEffect(() => {
    if (!isProcessing(detail)) return
    const timeoutId = window.setTimeout(async () => {
      try {
        const nextDetail = await loadDetail(true)
        await onLifecycleChange?.()
        if (!nextDetail || isProcessing(nextDetail)) return
        const completedAction = activeActionRef.current
        if (completedAction) finishLifecycleAction(completedAction, nextDetail)
      } catch (error) {
        console.error("[KB] poll tabular detail failed", error)
      }
    }, 1500)
    return () => window.clearTimeout(timeoutId)
  }, [detail, finishLifecycleAction, loadDetail, onLifecycleChange])

  const runLifecycleAction = useCallback(
    async (action: LifecycleAction) => {
      setIsMutating(true)
      try {
        if (action === "refresh") {
          const response = await refreshSource({ urlBuilderParams })
          if (!response?.data?.refresh_started) {
            successToast({
              description: t("knowledgeBase.treeView.tabular.alreadyCurrent"),
            })
            await loadDetail(true)
            await onLifecycleChange?.()
            return
          }
        } else if (action === "retry") {
          await retrySource({ urlBuilderParams })
        }
        activeActionRef.current = action
        const nextDetail = await loadDetail(true)
        await onLifecycleChange?.()
        if (nextDetail && !isProcessing(nextDetail)) {
          finishLifecycleAction(action, nextDetail)
        }
      } catch (error) {
        console.error(`[KB] tabular ${action} failed`, error)
        alertToast({
          ...getUserFacingApiErrorToastProps(error, t, {
            title: t("knowledgeBase.treeView.tabular.actionFailed"),
            fallbackDescription: t(
              "knowledgeBase.treeView.tabular.actionFailedDescription"
            ),
          }),
        })
      } finally {
        setIsMutating(false)
      }
    },
    [
      alertToast,
      finishLifecycleAction,
      loadDetail,
      onLifecycleChange,
      refreshSource,
      retrySource,
      successToast,
      t,
      urlBuilderParams,
    ]
  )

  const handleCandidateFile = (file: File | undefined) => {
    if (!file || !capabilities || !sourceKind) return
    const expectedExtension = sourceKind === "csv" ? ".csv" : ".xlsx"
    const validationFailure = getTabularFileValidationFailure(
      file,
      capabilities
    )
    if (
      getTabularFileExtension(file.name) !== expectedExtension ||
      validationFailure
    ) {
      alertToast({
        title: t("knowledgeBase.treeView.tabular.formatMismatchTitle"),
        description: t(
          `knowledgeBase.treeView.tabular.${sourceKind}FormatMismatch`
        ),
      })
      return
    }
    setCandidateFile(file)
    setIsReplaceDialogOpen(true)
  }

  const confirmReplace = async () => {
    if (!candidateFile) return
    setIsMutating(true)
    try {
      const form = new FormData()
      form.append("file", candidateFile)
      await replaceSource({ urlBuilderParams, body: form })
      activeActionRef.current = "replace"
      setIsReplaceDialogOpen(false)
      setCandidateFile(null)
      const nextDetail = await loadDetail(true)
      await onLifecycleChange?.()
      if (nextDetail && !isProcessing(nextDetail)) {
        finishLifecycleAction("replace", nextDetail)
      }
    } catch (error) {
      console.error("[KB] replace tabular source failed", error)
      alertToast({
        ...getUserFacingApiErrorToastProps(error, t, {
          title: t("knowledgeBase.treeView.tabular.replaceFailed"),
          fallbackDescription: t(
            "knowledgeBase.treeView.tabular.replaceFailedDescription"
          ),
        }),
      })
    } finally {
      setIsMutating(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-border bg-background p-4 text-sm text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
        {t("knowledgeBase.treeView.tabular.loading")}
      </div>
    )
  }

  if (!detail || !sourceKind) return null

  const processing = isProcessing(detail)
  const failed = detail.processing_state === "FAILED"
  const statusIcon = processing ? (
    <Loader2 className="size-4 animate-spin" />
  ) : failed ? (
    <AlertTriangle className="size-4" />
  ) : (
    <CheckCircle2 className="size-4" />
  )
  const statusKey = processing
    ? sourceKind === "googleSheet"
      ? "refreshing"
      : "processing"
    : failed
      ? "failed"
      : "ready"

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-border bg-background p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="space-y-1">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground">
              {statusIcon}
              {t(`knowledgeBase.treeView.tabular.status.${statusKey}`)}
            </div>
            <p className="text-xs text-muted-foreground">
              {processing && detail.available
                ? t("knowledgeBase.treeView.tabular.processingWithLastGood")
                : failed && detail.available
                  ? t("knowledgeBase.treeView.tabular.failureWithLastGood")
                  : failed
                    ? t("knowledgeBase.treeView.tabular.failureWithoutLastGood")
                    : t("knowledgeBase.treeView.tabular.readyDescription")}
            </p>
            {detail.last_processing_error && failed && (
              <p className="text-xs text-destructive">
                {detail.last_processing_error}
              </p>
            )}
          </div>
          <Badge variant="secondary">
            {t(`knowledgeBase.treeView.tabular.source.${sourceKind}`)}
          </Badge>
        </div>

        {canEdit && detail.feature_enabled && (
          <div className="mt-4 flex flex-wrap gap-2">
            {(sourceKind === "csv" || sourceKind === "xlsx") && (
              <>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={isMutating || processing}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="mr-2 size-4" />
                  {t(
                    `knowledgeBase.treeView.tabular.replace${sourceKind === "csv" ? "Csv" : "Xlsx"}`
                  )}
                </Button>
                <input
                  ref={fileInputRef}
                  className="hidden"
                  type="file"
                  accept={
                    sourceKind === "csv"
                      ? ".csv,text/csv"
                      : ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  }
                  onChange={(event) => {
                    handleCandidateFile(event.target.files?.[0])
                    event.target.value = ""
                  }}
                />
              </>
            )}
            {sourceKind === "googleSheet" && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isMutating || processing}
                onClick={() => void runLifecycleAction("refresh")}
              >
                <RefreshCw className="mr-2 size-4" />
                {t("knowledgeBase.treeView.tabular.refresh")}
              </Button>
            )}
            {failed && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={isMutating}
                onClick={() => void runLifecycleAction("retry")}
              >
                <RotateCcw className="mr-2 size-4" />
                {t("knowledgeBase.treeView.tabular.retry")}
              </Button>
            )}
          </div>
        )}
      </div>

      {detail.source_last_checked_at && sourceKind === "googleSheet" && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Clock3 className="size-3.5" />
          {t("knowledgeBase.treeView.tabular.lastChecked", {
            time: new Date(detail.source_last_checked_at).toLocaleString(),
          })}
        </div>
      )}

      {detail.available && (
        <section
          className="space-y-3"
          aria-label={t("knowledgeBase.treeView.tabular.schemaTitle")}
        >
          <div>
            <h4 className="text-sm font-medium text-foreground">
              {t("knowledgeBase.treeView.tabular.schemaTitle")}
            </h4>
            <p className="text-xs text-muted-foreground">
              {t("knowledgeBase.treeView.tabular.schemaDescription")}
            </p>
          </div>
          {detail.tables.map((table) => (
            <div
              key={table.id}
              className="overflow-hidden rounded-lg border border-border bg-background"
            >
              <div className="flex items-center justify-between gap-3 border-b border-border bg-muted/40 px-4 py-3">
                <div className="flex min-w-0 items-center gap-2">
                  <Database className="size-4 shrink-0 text-muted-foreground" />
                  <span className="truncate text-sm font-medium">
                    {table.name}
                  </span>
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {t("knowledgeBase.treeView.tabular.rowCount", {
                    count: table.row_count,
                  })}
                </span>
              </div>
              <div className="divide-y divide-border">
                {table.columns.map((column) => (
                  <div
                    key={column.id}
                    className="grid grid-cols-[minmax(0,1fr)_auto_auto] items-center gap-3 px-4 py-2.5 text-sm"
                  >
                    <span className="truncate font-medium" title={column.name}>
                      {column.name}
                    </span>
                    <Badge variant="outline" className="font-mono text-[11px]">
                      {column.type}
                    </Badge>
                    {column.nullable && (
                      <span className="text-xs text-muted-foreground">
                        {t(
                          "knowledgeBase.treeView.tabular.containsEmptyValues"
                        )}
                      </span>
                    )}
                  </div>
                ))}
              </div>
              {table.warnings.map((warning, warningIndex) => (
                <div
                  key={`${table.id}-${warning.code}-${warningIndex}`}
                  className="border-t border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100"
                >
                  <p>
                    {uiT(
                      `chat.reasoningSteps.tabular.warnings.${warning.code}`,
                      warning.message
                    )}
                  </p>
                  {warning.recovery_hint && (
                    <p className="mt-1 opacity-80">
                      {uiT(
                        `chat.reasoningSteps.tabular.warningRecovery.${warning.code}`,
                        warning.recovery_hint
                      )}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ))}
        </section>
      )}

      {detail.warnings.map((warning, warningIndex) => (
        <div
          key={`${warning.code}-${warningIndex}`}
          className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-100"
        >
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <div>
            <p>
              {uiT(
                `chat.reasoningSteps.tabular.warnings.${warning.code}`,
                warning.message
              )}
            </p>
            {warning.recovery_hint && (
              <p className="mt-1 opacity-80">
                {uiT(
                  `chat.reasoningSteps.tabular.warningRecovery.${warning.code}`,
                  warning.recovery_hint
                )}
              </p>
            )}
          </div>
        </div>
      ))}

      <Dialog
        open={isReplaceDialogOpen}
        onOpenChange={(open) => {
          if (isMutating) return
          setIsReplaceDialogOpen(open)
          if (!open) setCandidateFile(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t(
                `knowledgeBase.treeView.tabular.replace${sourceKind === "csv" ? "Csv" : "Xlsx"}`
              )}
            </DialogTitle>
            <DialogDescription>
              {t("knowledgeBase.treeView.tabular.replaceConfirmation", {
                structure: t(
                  sourceKind === "csv"
                    ? "knowledgeBase.treeView.tabular.csvStructure"
                    : "knowledgeBase.treeView.tabular.xlsxStructure"
                ),
              })}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-md border border-border bg-muted/40 p-3 text-sm">
            <p className="font-medium">{candidateFile?.name}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              {t(`knowledgeBase.treeView.tabular.${sourceKind}ReplaceHelper`)}
            </p>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              disabled={isMutating}
              onClick={() => setIsReplaceDialogOpen(false)}
            >
              {t("knowledgeBase.treeView.cancel")}
            </Button>
            <Button
              type="button"
              disabled={isMutating}
              onClick={() => void confirmReplace()}
            >
              {isMutating && <Loader2 className="mr-2 size-4 animate-spin" />}
              {t("common.confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
